import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Download } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";
import {
  api, FacilityDetail, FacilityScores, ScoresExplain, ScoreIndexExplain,
  ScoreHistory, ScoreHistoryEntry,
} from "../lib/api";
import { HelpButton } from "../components/HelpButton";
import { FacilityCharts } from "../components/FacilityCharts";
import { FacilityNetworkMap } from "../components/FacilityNetworkMap";

const INDEX_COLORS: Record<string, string> = {
  score_activity: "#dd6b20",
  score_efficiency: "#38a169",
  score_care_routing: "#3182ce",
  score_financial_health: "#d69e2e",
  score_dynamics: "#805ad5",
  score_composite_default: "#1a365d",
};

type FacilityEvent = {
  date: string;
  icon: string;
  label: string;
  detail: string;
  color: string;
  group: "structural" | "score";
};

const SCORE_INDICES: { key: keyof ScoreHistoryEntry; label: string }[] = [
  { key: "score_activity", label: "Активність" },
  { key: "score_efficiency", label: "Ефективність" },
  { key: "score_care_routing", label: "Маршрутизація" },
  { key: "score_financial_health", label: "Фін. стійкість" },
  { key: "score_dynamics", label: "Динаміка" },
];

const SCORE_DELTA_THRESHOLD = 15;  // різке зростання/падіння одного з 5 балів — alert

function detectEvents(history: ScoreHistoryEntry[]): FacilityEvent[] {
  const events: FacilityEvent[] = [];
  for (let i = 1; i < history.length; i++) {
    const prev = history[i - 1];
    const curr = history[i];

    // 1. Зміна peer-групи (рідко, але важливо для контексту порівнянь)
    if (prev.peer_group_key && curr.peer_group_key && prev.peer_group_key !== curr.peer_group_key) {
      events.push({
        date: curr.snapshot_date,
        icon: "🔵",
        label: "Зміна peer-групи",
        detail: `${prev.peer_group_key} → ${curr.peer_group_key}`,
        color: "#3182ce",
        group: "structural",
      });
    }

    // 2. Зміна coverage пакетів НСЗУ
    if (prev.coverage_packages !== curr.coverage_packages) {
      const delta = curr.coverage_packages - prev.coverage_packages;
      events.push({
        date: curr.snapshot_date,
        icon: delta > 0 ? "📦＋" : "📦−",
        label: delta > 0 ? `Додано пакет` : `Знято пакет`,
        detail: `Охоплення пакетів: ${prev.coverage_packages} → ${curr.coverage_packages}`,
        color: delta > 0 ? "#38a169" : "#e53e3e",
        group: "structural",
      });
    }

    // 3. Перехід data_sufficient
    if (prev.data_sufficient !== curr.data_sufficient) {
      events.push({
        date: curr.snapshot_date,
        icon: curr.data_sufficient ? "✅" : "⚠️",
        label: curr.data_sufficient ? "Стало достатньо даних" : "Стало мало даних",
        detail: `data_sufficient: ${prev.data_sufficient} → ${curr.data_sufficient}`,
        color: curr.data_sufficient ? "#38a169" : "#d69e2e",
        group: "structural",
      });
    }

    // 4. Composite перетнув 50 / 75 (порогові події, видно одразу настрій)
    const cp = prev.score_composite_default;
    const cc = curr.score_composite_default;
    if (cp != null && cc != null) {
      if (cp >= 50 && cc < 50) {
        events.push({
          date: curr.snapshot_date, icon: "📉",
          label: "Composite впав нижче 50",
          detail: `${cp.toFixed(1)} → ${cc.toFixed(1)}`,
          color: "#e53e3e", group: "score",
        });
      }
      if (cp < 50 && cc >= 50) {
        events.push({
          date: curr.snapshot_date, icon: "📈",
          label: "Composite перевищив 50",
          detail: `${cp.toFixed(1)} → ${cc.toFixed(1)}`,
          color: "#38a169", group: "score",
        });
      }
      if (cp < 75 && cc >= 75) {
        events.push({
          date: curr.snapshot_date, icon: "🌟",
          label: "Composite перевищив 75",
          detail: `${cp.toFixed(1)} → ${cc.toFixed(1)}`,
          color: "#38a169", group: "score",
        });
      }
      if (cp >= 75 && cc < 75) {
        events.push({
          date: curr.snapshot_date, icon: "💫",
          label: "Composite опустився нижче 75",
          detail: `${cp.toFixed(1)} → ${cc.toFixed(1)}`,
          color: "#d69e2e", group: "score",
        });
      }
    }

    // 5. Різкий drop/rise одного з 5 балів (>= 15 пунктів за місяць)
    for (const { key, label } of SCORE_INDICES) {
      const p = prev[key] as number | null;
      const c = curr[key] as number | null;
      if (p == null || c == null) continue;
      const diff = c - p;
      if (diff <= -SCORE_DELTA_THRESHOLD) {
        events.push({
          date: curr.snapshot_date, icon: "🚨",
          label: `${label}: різке падіння`,
          detail: `${p.toFixed(1)} → ${c.toFixed(1)} (${diff.toFixed(1)})`,
          color: "#e53e3e", group: "score",
        });
      } else if (diff >= SCORE_DELTA_THRESHOLD) {
        events.push({
          date: curr.snapshot_date, icon: "⭐",
          label: `${label}: різке зростання`,
          detail: `${p.toFixed(1)} → ${c.toFixed(1)} (+${diff.toFixed(1)})`,
          color: "#38a169", group: "score",
        });
      }
    }
  }
  return events;
}

const INDEX_LABEL: Record<string, string> = {
  activity: "Активність",
  efficiency: "Ефективність",
  care_routing: "Маршрутизація",
  financial_health: "Фін. стійкість",
  dynamics: "Динаміка",
};

// Людські лейбли + короткий опис для inputs у breakdown.
// Якщо поля немає в мапі — просто покажемо ключ (fallback).
// "Ця лікарня" = заклад, картку якого зараз відкрито.
// "Peer-група" = лікарні з тією ж областю + типом догляду.
const INPUT_LABELS: Record<string, { label: string; hint?: string; suffix?: string }> = {
  // Спільне для percentile-rank індексів
  peer_size: { label: "Розмір peer-групи", hint: "всього лікарень у peer-групі (область + тип догляду)" },
  peers_with_data: { label: "Peer-лікарень з даними", hint: "скільки з peer-групи мають дані для розрахунку" },
  reason: { label: "Чому бал ще не розраховано" },

  // Activity
  my_total_services: { label: "Послуг (ця лікарня)", hint: "загальна кількість послуг за всю історію" },
  peer_min: { label: "Мінімум послуг у peer-групі", hint: "найменш активна лікарня peer-групи" },
  peer_p25: { label: "Нижня чверть peer-групи", hint: "25% peer-лікарень роблять менше" },
  peer_median: { label: "Медіана peer-групи", hint: "типова лікарня peer-групи за обсягом послуг" },
  peer_p75: { label: "Верхня чверть peer-групи", hint: "лише 25% peer-лікарень роблять більше" },
  peer_max: { label: "Максимум послуг у peer-групі", hint: "найактивніша лікарня peer-групи" },
  less_than_me: { label: "Peer-лікарень з меншим обсягом", hint: "у скількох peer-лікарень послуг менше, ніж у цієї" },
  equal_to_me: { label: "Peer-лікарень з таким же обсягом", hint: "peer-лікарень з такою ж кількістю послуг" },

  // Efficiency
  my_pay_all_uah: { label: "Сума виплат (ця лікарня)", hint: "усі виплати з ПМГ за весь час", suffix: "₴" },
  my_episodes: { label: "Кількість епізодів (ця лікарня)", hint: "загальна кількість лікувальних випадків" },
  my_cost_per_episode_uah: { label: "Вартість епізоду (ця лікарня)", hint: "виплати ÷ кількість епізодів", suffix: "₴" },
  cheaper_than_me_count: { label: "Peer-лікарень дешевших за цю", hint: "peer-лікарень, у яких вартість одного епізоду нижча" },
  raw_percentile: { label: "Місце в peer-групі за вартістю", hint: "позиція цієї лікарні в peer-групі; нижча вартість = вищий бал" },

  // Care Routing (ESOZ направлення)
  my_created_referrals: { label: "Створено направлень (ця лікарня)" },
  my_completed_referrals: { label: "Виконано направлень (ця лікарня)" },
  my_recalled_referrals: { label: "Відкликано направлень (ця лікарня)" },
  my_error_referrals: { label: "Направлень з помилкою (ця лікарня)" },
  my_completion_rate_pct: { label: "Частка виконаних (ця лікарня)", hint: "виконано ÷ створено", suffix: "%" },
  peer_min_rate_pct: { label: "Мінімум частки виконаних у peer-групі", suffix: "%" },
  peer_median_rate_pct: { label: "Медіана частки виконаних у peer-групі", suffix: "%" },
  peer_max_rate_pct: { label: "Максимум частки виконаних у peer-групі", suffix: "%" },

  // Financial Health (форма 1-нс)
  my_period: { label: "Період останнього звіту (ця лікарня)" },
  my_revenue_uah: { label: "Доходи (ця лікарня, останній звіт)", suffix: "₴" },
  my_expense_uah: { label: "Витрати (ця лікарня, останній звіт)", suffix: "₴" },
  my_revenue_expense_ratio: { label: "Доходи/витрати (ця лікарня)", hint: "> 1 = доходи перевищують витрати" },
  peer_min_ratio: { label: "Мінімум доходи/витрати в peer-групі" },
  peer_median_ratio: { label: "Медіана доходи/витрати peer-групи" },
  peer_max_ratio: { label: "Максимум доходи/витрати в peer-групі" },

  // Dynamics
  months_of_data: { label: "Місяців історії", hint: "скільки точок для тренду" },
  first_month: { label: "Перший місяць даних" },
  last_month: { label: "Останній місяць даних" },
  first_value: { label: "Послуг у перший місяць" },
  last_value: { label: "Послуг в останній місяць" },
  mean_value: { label: "Середня кількість послуг/міс" },
  slope_per_month: { label: "Нахил тренду", hint: "у скільки послуг на місяць зростає/спадає" },
  pct_change_per_month: { label: "Темп росту", hint: "відсоток зміни на місяць", suffix: "%/міс" },
};

const STATUS_LABELS: Record<string, string> = {
  active: "Діюча",
  war_zone: "Зона бойових дій",
  relocated: "Переміщена",
  new: "Нова",
  closed: "Закрита",
};

function fmtInputValue(v: any): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("uk-UA");
  return String(v);
}

const SCALE_LABELS: Record<string, { label: string; anchor: string }> = {
  score_activity: { label: "Активність", anchor: "scale-1" },
  score_efficiency: { label: "Ефективність", anchor: "scale-2" },
  score_care_routing: { label: "Маршрутизація", anchor: "scale-3" },
  score_financial_health: { label: "Фін. стійкість", anchor: "scale-4" },
  score_dynamics: { label: "Динаміка", anchor: "scale-5" },
};

export function FacilityDetailPage() {
  const { edrpou } = useParams<{ edrpou: string }>();
  const [tab, setTab] = useState<"overview" | "charts">("overview");
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!edrpou) return;
    setExporting(true);
    try {
      await api.download(`/api/v1/facilities/${edrpou}/export.xlsx`);
    } catch (err: any) {
      alert(err?.detail || err?.message || "Помилка експорту");
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["facility", edrpou],
    queryFn: () => api.get<FacilityDetail>(`/api/v1/facilities/${edrpou}`),
    enabled: !!edrpou,
  });
  const { data: scores } = useQuery({
    queryKey: ["facility-scores", edrpou],
    queryFn: () => api.get<FacilityScores>(`/api/v1/facilities/${edrpou}/scores`).catch(() => null),
    enabled: !!edrpou,
  });
  const { data: explain } = useQuery({
    queryKey: ["facility-scores-explain", edrpou],
    queryFn: () =>
      api.get<ScoresExplain>(`/api/v1/facilities/${edrpou}/scores/explain`).catch(() => null),
    enabled: !!edrpou,
  });
  const { data: scoresHistory } = useQuery({
    queryKey: ["facility-scores-history", edrpou],
    queryFn: () =>
      api.get<ScoreHistory>(`/api/v1/facilities/${edrpou}/scores/history`).catch(() => null),
    enabled: !!edrpou,
  });

  if (isLoading) return <div style={{ padding: 24 }}>Завантаження…</div>;
  if (error) return <div style={{ padding: 24, color: "#c53030" }}>Помилка: {(error as any)?.detail}</div>;
  if (!data) return null;

  return (
    <div style={{ padding: "24px 32px" }}>
      <Link to="/facilities" style={{ color: "#4299e1", textDecoration: "none", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <ArrowLeft size={14} /> Назад
      </Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, margin: "12px 0 4px" }}>
        <h1 style={{ fontSize: 24, color: "#1a365d", margin: 0 }}>{data.name}</h1>
        <button
          onClick={handleExport}
          disabled={exporting || !scores}
          title={!scores
            ? "Бали ще не розраховані для цієї лікарні — експорт стане доступним після recalc"
            : "Завантажити Excel з усіма показниками та сирими даними"}
          style={{
            padding: "8px 14px",
            background: !scores ? "#a0aec0" : "#38a169",
            color: "white", border: "none", borderRadius: 4,
            fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
            cursor: (!scores || exporting) ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
            opacity: exporting ? 0.7 : 1,
          }}
        >
          <Download size={14} /> {exporting ? "Експортується…" : "Експорт у Excel"}
        </button>
      </div>
      <div style={{ color: "#718096", fontSize: 13, marginBottom: 12 }}>
        ЄДРПОУ {data.edrpou} · {data.area || "—"} · {data.care_type || "—"} · {data.network_role || "—"} · статус: <strong>{STATUS_LABELS[data.status] || data.status}</strong>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {(["overview", "charts"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "8px 16px", background: "none", border: "none",
              borderBottom: tab === t ? "3px solid #2c5282" : "3px solid transparent",
              marginBottom: -2, cursor: "pointer", fontSize: 14,
              fontWeight: tab === t ? 600 : 400,
              color: tab === t ? "#2c5282" : "#718096",
            }}
          >
            {t === "overview" ? "📊 Огляд та бали" : "📈 Графіки та динаміка"}
          </button>
        ))}
      </div>

      {tab === "charts" ? (
        <FacilityCharts edrpou={edrpou!} />
      ) : (
      <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 24 }}>
        <Card title="Загальне">
          <Field label="Статус">{STATUS_LABELS[data.status] || data.status}</Field>
          <Field label="Роль у мережі">{data.network_role || "—"}</Field>
          <Field label="Форма власності">{data.property_type || "—"}</Field>
          <Field label="Керівник">{data.owner_name || "—"}</Field>
        </Card>
        <Card title="Контакти">
          <Field label="Email">{data.email || "—"}</Field>
          <Field label="Телефон">{data.phone || "—"}</Field>
          <Field label="Сайт">{data.website || "—"}</Field>
        </Card>
        <Card title="Адреса">
          <Field label="Громада">{data.gromada_name || "—"}</Field>
          <Field label="Нас. пункт">{data.settlement_name || "—"}</Field>
          <Field label="Адреса">{data.address || "—"}</Field>
          <Field label="Координати">
            {data.lat != null && data.lng != null
              ? `${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`
              : "—"}{" "}
            {data.geocoded_via && (
              <span style={{ fontSize: 11, color: "#a0aec0" }}>📍 {data.geocoded_via}</span>
            )}
          </Field>
        </Card>
      </div>

      <Card title={<>5 індексів рейтингу <HelpButton anchor="composite" /></>}>
        {!scores && (
          <div style={{ color: "#a0aec0", fontSize: 13 }}>
            Бали ще не розраховані для цього закладу. Адміністратор може запустити перерахунок через
            <code style={{ marginLeft: 4 }}>POST /api/v1/scores/recalc</code>.
          </div>
        )}
        {scores && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              {(Object.keys(SCALE_LABELS) as (keyof typeof SCALE_LABELS)[]).map((k) => {
                const v = (scores as any)[k] as number | null;
                const cfg = SCALE_LABELS[k];
                return (
                  <ScoreCell
                    key={k}
                    label={cfg.label}
                    value={v}
                    anchor={cfg.anchor}
                    history={scoresHistory?.history}
                    scoreKey={k}
                  />
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, padding: "12px 0", borderTop: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 13, color: "#4a5568" }}>
                <strong>Composite</strong> <HelpButton anchor="composite" />
                <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>
                  Peer-група: {scores.peer_group_key} · {scores.data_sufficient ? "✅ даних достатньо" : "⚠ мало даних"} · охоплення пакетів: {scores.coverage_packages}
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: scoreColor(scores.score_composite_default) }}>
                {scores.score_composite_default ?? "—"}
              </div>
            </div>
            {scores.note && (
              <div style={{ marginTop: 12, padding: 8, background: "#fffbf0", border: "1px solid #f6e05e", borderRadius: 4, fontSize: 12, color: "#744210" }}>
                ⓘ {scores.note}
              </div>
            )}
            {scores.sub_scores && Object.keys(scores.sub_scores).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#2c5282", marginBottom: 8 }}>
                  Sub-scores по фокусних пакетах:
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#718096" }}>
                      <th style={{ padding: "4px 8px" }}>Пакет</th>
                      <th style={{ padding: "4px 8px", textAlign: "right" }}>Загалом послуг</th>
                      <th style={{ padding: "4px 8px", textAlign: "right" }}>Бал активності</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(scores.sub_scores).map(([k, v]) => (
                      <tr key={k} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "4px 8px" }}>{k.replace("package_", "Пакет ")}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>{v.total.toLocaleString("uk-UA")}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right", color: scoreColor(v.activity) }}>{v.activity.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {scoresHistory && scoresHistory.history.length >= 2 && (
        <Card title={<>📈 Динаміка 5 індексів за {scoresHistory.history.length} замірів</>}>
          <div style={{ fontSize: 12, color: "#718096", marginBottom: 12 }}>
            Кожна точка — замір станом на вказану дату; ковзне 12-міс. вікно. Composite —
            пунктиром. Місяці без даних пропущені на лінії.
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={scoresHistory.history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
              <XAxis dataKey="snapshot_date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtDate} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="score_activity" stroke={INDEX_COLORS.score_activity}
                    strokeWidth={2} name="Активність" dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="score_efficiency" stroke={INDEX_COLORS.score_efficiency}
                    strokeWidth={2} name="Ефективність" dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="score_care_routing" stroke={INDEX_COLORS.score_care_routing}
                    strokeWidth={2} name="Маршрутизація" dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="score_financial_health" stroke={INDEX_COLORS.score_financial_health}
                    strokeWidth={2} name="Фін. стійкість" dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="score_dynamics" stroke={INDEX_COLORS.score_dynamics}
                    strokeWidth={2} name="Динаміка" dot={{ r: 2 }} connectNulls={false} />
              <Line type="monotone" dataKey="score_composite_default" stroke={INDEX_COLORS.score_composite_default}
                    strokeWidth={2} strokeDasharray="4 2" name="Composite" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>

          <EventsTimeline history={scoresHistory.history} />
        </Card>
      )}

      {explain && (
        <Card title={<>🔍 Як розраховуються бали? <span style={{ fontSize: 12, fontWeight: 400, color: "#718096" }}>(розкрийте кожен індекс)</span></>}>
          <div style={{ background: "#f7fafc", padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 12.5, color: "#4a5568" }}>
            <strong>Peer-група:</strong> <code>{explain.peer_group.key}</code> — порівнюємо з <strong>{explain.peer_group.size}</strong> схожими лікарнями
            {" "}({explain.peer_group.members_with_services_data} з даними).
          </div>
          {(["activity", "efficiency", "dynamics", "care_routing", "financial_health"] as const).map((k) => (
            <BreakdownAccordion key={k} title={INDEX_LABEL[k]} idx={explain.indices[k]} />
          ))}
          <div style={{ marginTop: 12, padding: 12, background: "#ebf8ff", borderRadius: 4, fontSize: 12.5 }}>
            <div style={{ fontWeight: 600, color: "#2c5282", marginBottom: 6 }}>
              Composite = {explain.composite.value} (формула: {explain.composite.formula})
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "#718096", textAlign: "left" }}>
                  <th style={{ padding: "4px 8px" }}>Індекс</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Бал</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>×</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Вага</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>=</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Внесок</th>
                </tr>
              </thead>
              <tbody>
                {explain.composite.contributions.map((c) => (
                  <tr key={c.index} style={{ borderTop: "1px solid #cbd5e0" }}>
                    <td style={{ padding: "4px 8px" }}>{INDEX_LABEL[c.index]}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", color: scoreColor(c.score) }}>{c.score}</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", color: "#a0aec0" }}>×</td>
                    <td style={{ padding: "4px 8px", textAlign: "right" }}>{(c.weight * 100).toFixed(0)}%</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", color: "#a0aec0" }}>=</td>
                    <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{c.contribution}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid #2c5282", fontWeight: 700 }}>
                  <td style={{ padding: "6px 8px" }} colSpan={5}>Сума:</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", color: "#2c5282" }}>
                    {explain.composite.value}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data.lat != null && data.lng != null && (
        <Card title="🗺 Мережа закладу на карті">
          <FacilityNetworkMap edrpou={data.edrpou} />
        </Card>
      )}

      {data.divisions && data.divisions.length > 0 && (
        <Card title={`Підрозділи (${data.divisions.length})`}>
          <DivisionsList divisions={data.divisions} />
        </Card>
      )}
      </>
      )}
    </div>
  );
}

function DivisionsList({ divisions }: { divisions: any[] }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showAll, setShowAll] = useState(false);

  const types = Array.from(new Set(divisions.map((d) => d.division_type).filter(Boolean))).sort();

  const filtered = divisions.filter((d) => {
    if (typeFilter && d.division_type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [
        d.division_name, d.residence_addresses, d.residence_settlement,
        d.division_phone, d.division_email,
      ].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const visible = showAll ? filtered : filtered.slice(0, 10);
  const hasMore = filtered.length > visible.length;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          placeholder="🔍 Пошук за назвою, адресою…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShowAll(false); }}
          style={{
            flex: "1 1 240px", padding: "6px 10px", fontSize: 12,
            border: "1px solid #cbd5e0", borderRadius: 4, outline: "none",
          }}
        />
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setShowAll(false); }}
          style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #cbd5e0", borderRadius: 4 }}
        >
          <option value="">всі типи ({divisions.length})</option>
          {types.map((t) => {
            const cnt = divisions.filter((d) => d.division_type === t).length;
            return <option key={t} value={t}>{t} ({cnt})</option>;
          })}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 16, color: "#a0aec0", fontSize: 12, textAlign: "center" }}>
          Нічого не знайдено за фільтрами
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "#a0aec0", marginBottom: 6 }}>
            Показано {visible.length} з {filtered.length}
            {(search || typeFilter) && ` · фільтр з ${divisions.length} загалом`}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {visible.map((d, i) => (
              <li
                key={d.division_id || i}
                style={{
                  padding: "10px 0",
                  borderTop: i > 0 ? "1px solid #e2e8f0" : "none",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600, color: "#2d3748" }}>
                  {d.division_name || d.name || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>
                  {d.division_type || "—"}
                </div>
                <div style={{ fontSize: 12, color: "#4a5568", marginTop: 4 }}>
                  📍 {d.residence_addresses || "—"}
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 11.5, color: "#718096", flexWrap: "wrap" }}>
                  {d.division_phone && <span>☎ {d.division_phone}</span>}
                  {d.division_email && <span>✉ {d.division_email}</span>}
                </div>
              </li>
            ))}
          </ul>
          {hasMore && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                marginTop: 12, padding: "6px 14px", background: "white",
                border: "1px solid #2c5282", color: "#2c5282", borderRadius: 4,
                cursor: "pointer", fontSize: 12, width: "100%",
              }}
            >
              Показати ще {filtered.length - visible.length} підрозділів
            </button>
          )}
          {showAll && filtered.length > 10 && (
            <button
              onClick={() => setShowAll(false)}
              style={{
                marginTop: 12, padding: "6px 14px", background: "white",
                border: "1px solid #cbd5e0", color: "#718096", borderRadius: 4,
                cursor: "pointer", fontSize: 12, width: "100%",
              }}
            >
              Згорнути
            </button>
          )}
        </>
      )}
    </div>
  );
}

function BreakdownAccordion({ title, idx }: { title: string; idx: ScoreIndexExplain }) {
  const [open, setOpen] = useState(false);
  const color = scoreColor(idx.value);
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 4, marginBottom: 6, background: "white" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 12px", background: open ? "#f7fafc" : "white", border: "none",
          cursor: "pointer", borderRadius: 4, fontSize: 13, textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <strong>{title}</strong>
          {idx.is_placeholder && <span style={{ fontSize: 11, color: "#a0aec0" }}>(не розраховано)</span>}
        </span>
        <span style={{ fontSize: 16, fontWeight: 700, color }}>{idx.value}</span>
      </button>
      {open && (
        <div style={{ padding: 12, fontSize: 12.5, color: "#2d3748", borderTop: "1px solid #e2e8f0" }}>
          <Row label="Метод" value={idx.method} />
          <Row label="Формула" value={<code style={codeStyle}>{idx.formula}</code>} />
          <Row label="Дані" value={<code style={codeStyle}>{idx.data_source}</code>} />
          <Row label="Інтерпретація" value={idx.interpretation} />
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #e2e8f0" }}>
            <div style={{ fontWeight: 600, color: "#2c5282", marginBottom: 6 }}>Сирі дані:</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <tbody>
                {Object.entries(idx.inputs).map(([k, v]) => {
                  const meta = INPUT_LABELS[k];
                  return (
                    <tr key={k} style={{ borderTop: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "5px 6px", color: "#2d3748", width: "55%" }}>
                        <div title={k}>{meta?.label || k}</div>
                        {meta?.hint && (
                          <div style={{ fontSize: 10.5, color: "#a0aec0", marginTop: 1 }}>
                            {meta.hint}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "5px 6px", textAlign: "right", color: "#2d3748", fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>
                        {fmtInputValue(v)}{meta?.suffix && <span style={{ color: "#718096", marginLeft: 3 }}>{meta.suffix}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", padding: "3px 0", gap: 8 }}>
      <div style={{ width: 110, color: "#718096", flexShrink: 0 }}>{label}:</div>
      <div style={{ flex: 1 }}>{value}</div>
    </div>
  );
}

const codeStyle: React.CSSProperties = {
  display: "inline-block", padding: "1px 6px", background: "#edf2f7",
  borderRadius: 3, fontSize: 11.5, color: "#2c5282", fontFamily: "ui-monospace, monospace",
};

function ScoreCell({
  label, value, anchor, history, scoreKey,
}: {
  label: string;
  value: number | null;
  anchor: string;
  history?: ScoreHistoryEntry[];
  scoreKey?: string;
}) {
  const color = scoreColor(value);
  const noData = value === null || value === undefined;
  const sparkData = history && scoreKey
    ? history
        .map(h => ({ d: h.snapshot_date, v: (h as any)[scoreKey] as number | null }))
        .filter(p => p.v !== null && p.v !== undefined)
    : [];
  return (
    <div
      style={{
        padding: 12, background: noData ? "#f7fafc" : "#f7fafc",
        borderRadius: 6,
        border: `2px solid ${noData ? "#e2e8f0" : color + "30"}`,
        opacity: noData ? 0.7 : 1,
      }}
      title={noData ? "Немає достатньо даних для розрахунку — деталі в акордеоні нижче" : undefined}
    >
      <div style={{ fontSize: 11, color: "#718096", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
        {label} <HelpButton anchor={anchor} />
      </div>
      {noData ? (
        <div style={{ fontSize: 12, fontWeight: 500, color: "#a0aec0", marginTop: 4 }}>
          немає даних
        </div>
      ) : (
        <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      )}
      {sparkData.length >= 2 && (
        <div style={{ marginTop: 8, height: 28, background: "#ffffff", borderRadius: 3, padding: "1px 2px" }}
             title={`Динаміка: ${sparkData.length} точок (наведи на головний графік для деталей)`}>
          <ResponsiveContainer width="100%" height={26}>
            <LineChart data={sparkData} margin={{ top: 1, right: 1, bottom: 1, left: 1 }}>
              <YAxis domain={[0, 100]} hide />
              <Line type="monotone" dataKey="v" stroke={color}
                    strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function fmtDate(d: string) {
  if (!d) return "";
  const [y, m] = d.split("-");
  const months = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];
  return `${months[parseInt(m, 10) - 1] || m} ${y.slice(2)}`;
}

function EventsTimeline({ history }: { history: ScoreHistoryEntry[] }) {
  const [showAll, setShowAll] = useState(false);
  const events = detectEvents(history);
  const structural = events.filter(e => e.group === "structural");
  const scoreEvents = events.filter(e => e.group === "score");

  if (events.length === 0) {
    return (
      <div style={{ marginTop: 12, padding: "10px 12px", background: "#f7fafc",
                    borderRadius: 4, fontSize: 12, color: "#a0aec0", textAlign: "center" }}>
        За цей період структурних змін і різких зрушень балів не зафіксовано.
      </div>
    );
  }

  const visible = showAll ? events : events.slice(0, 8);
  const hasMore = events.length > visible.length;

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#2c5282", marginBottom: 4,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span>📌 Опорні події ({events.length})</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: "#718096" }}>
          структурних: {structural.length} · зрушень балів: {scoreEvents.length}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "#a0aec0", marginBottom: 8 }}>
        Зміни peer-групи, охоплення пакетів, достатності даних, перетин Composite порогів
        50/75, та різкі стрибки балів (≥{SCORE_DELTA_THRESHOLD} пунктів за місяць).
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {visible.map((e, i) => (
          <li key={`${e.date}-${e.label}-${i}`}
              style={{
                padding: "5px 8px",
                borderTop: i > 0 ? "1px solid #f0f0f0" : "none",
                display: "flex", gap: 8, alignItems: "center",
                background: i % 2 === 0 ? "#fafbfc" : "white",
              }}>
            <span style={{ width: 56, color: "#718096", fontFamily: "ui-monospace, monospace",
                            fontSize: 11, flexShrink: 0 }}>
              {fmtDate(e.date)}
            </span>
            <span style={{ width: 22, fontSize: 13, flexShrink: 0, textAlign: "center" }}>
              {e.icon}
            </span>
            <span style={{ minWidth: 180, fontWeight: 600, color: e.color, flexShrink: 0 }}>
              {e.label}
            </span>
            <span style={{ flex: 1, color: "#4a5568" }}>{e.detail}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 8, padding: "5px 10px", background: "white",
            border: "1px solid #cbd5e0", color: "#4a5568", borderRadius: 4,
            cursor: "pointer", fontSize: 11.5, width: "100%",
          }}>
          Показати ще {events.length - visible.length} події
        </button>
      )}
      {showAll && events.length > 8 && (
        <button
          onClick={() => setShowAll(false)}
          style={{
            marginTop: 8, padding: "5px 10px", background: "white",
            border: "1px solid #e2e8f0", color: "#a0aec0", borderRadius: 4,
            cursor: "pointer", fontSize: 11.5, width: "100%",
          }}>
          Згорнути
        </button>
      )}
    </div>
  );
}

function scoreColor(v: number | null | undefined): string {
  if (v === null || v === undefined) return "#a0aec0";
  if (v >= 75) return "#38a169";
  if (v >= 50) return "#d69e2e";
  if (v >= 25) return "#dd6b20";
  return "#e53e3e";
}

function Card({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: "white", padding: 16, border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#2c5282", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", padding: "4px 0", fontSize: 13 }}>
      <div style={{ width: 130, color: "#718096" }}>{label}:</div>
      <div style={{ flex: 1, color: "#2d3748" }}>{children}</div>
    </div>
  );
}
